/**
 * Factory para Serviços de Plataforma
 * Registra e gerencia todas as implementações de serviços
 */

import { BasePlatformService } from './interfaces/base_platform_service';
import { IPlatformDetectionService } from './interfaces/platform_detection_service';
import { IPermissionService } from './interfaces/permission_service';
import { IStorageService } from './interfaces/storage_service';
import { INetworkService } from './interfaces/network_service';
import { IFeatureService } from './interfaces/feature_service';

// Implementações
import { PlatformDetectionSystem } from './system/platform_detection_system';
import { PermissionSystem } from './system/permission_system';
import { StorageSystem } from './system/storage_system';
import { NetworkSystem } from './system/network_system';
import { FeatureSystem } from './system/feature_system';

export type ServiceType = 
  | 'PlatformDetection'
  | 'Permission'
  | 'Storage'
  | 'Network'
  | 'Feature';

export interface ServiceRegistry {
  PlatformDetection: IPlatformDetectionService;
  Permission: IPermissionService;
  Storage: IStorageService;
  Network: INetworkService;
  Feature: IFeatureService;
}

export class PlatformServiceFactory {
  private static instance: PlatformServiceFactory;
  private services = new Map<ServiceType, BasePlatformService>();
  private isInitialized = false;

  private constructor() {
    this.registerServices();
  }

  static getInstance(): PlatformServiceFactory {
    if (!PlatformServiceFactory.instance) {
      PlatformServiceFactory.instance = new PlatformServiceFactory();
    }
    return PlatformServiceFactory.instance;
  }

  /**
   * Registra todas as implementações de serviços
   */
  private registerServices(): void {
    console.log('📋 Registrando serviços de plataforma...');

    // Detecção de Plataforma
    this.services.set('PlatformDetection', new PlatformDetectionSystem());
    
    // Gerenciamento de Permissões
    this.services.set('Permission', new PermissionSystem());
    
    // Sistema de Armazenamento
    this.services.set('Storage', new StorageSystem());
    
    // Sistema de Rede
    this.services.set('Network', new NetworkSystem());
    
    // Sistema de Features
    this.services.set('Feature', new FeatureSystem());

    console.log(`✅ ${this.services.size} serviços registrados`);
  }

  /**
   * Obtém um serviço específico
   */
  getService<T extends ServiceType>(type: T): ServiceRegistry[T] {
    const service = this.services.get(type);
    if (!service) {
      throw new Error(`Serviço '${type}' não encontrado. Certifique-se de que está registrado.`);
    }
    return service as ServiceRegistry[T];
  }

  /**
   * Verifica se um serviço está disponível
   */
  hasService(type: ServiceType): boolean {
    return this.services.has(type);
  }

  /**
   * Lista todos os serviços registrados
   */
  getRegisteredServices(): ServiceType[] {
    return Array.from(this.services.keys());
  }

  /**
   * Inicializa todos os serviços
   */
  async initializeAll(): Promise<void> {
    if (this.isInitialized) {
      console.log('⚠️ Serviços já inicializados');
      return;
    }

    console.log('🚀 Inicializando todos os serviços de plataforma...');

    const initPromises = Array.from(this.services.values()).map(async (service) => {
      try {
        await service.initialize();
        console.log(`✅ ${service.serviceName} inicializado`);
      } catch (error) {
        console.error(`❌ Erro ao inicializar ${service.serviceName}:`, error);
        throw error;
      }
    });

    await Promise.all(initPromises);
    this.isInitialized = true;

    console.log('🎉 Todos os serviços de plataforma inicializados!');
  }

  /**
   * Inicializa serviços específicos
   */
  async initializeServices(types: ServiceType[]): Promise<void> {
    console.log(`🔧 Inicializando serviços específicos: ${types.join(', ')}`);

    const initPromises = types.map(async (type) => {
      const service = this.getService(type);
      if (!service.isInitialized) {
        await service.initialize();
        console.log(`✅ ${service.serviceName} inicializado`);
      }
    });

    await Promise.all(initPromises);
  }

  /**
   * Obtém status de todos os serviços
   */
  getServicesStatus(): Record<ServiceType, any> {
    const status: any = {};
    
    for (const [type, service] of this.services) {
      status[type] = service.getStatus();
    }
    
    return status;
  }

  /**
   * Cleanup de todos os serviços
   */
  cleanup(): void {
    console.log('🧹 Limpando todos os serviços de plataforma...');

    for (const [type, service] of this.services) {
      try {
        service.cleanup();
        console.log(`🧹 ${service.serviceName} limpo`);
      } catch (error) {
        console.error(`❌ Erro ao limpar ${service.serviceName}:`, error);
      }
    }

    this.isInitialized = false;
    console.log('✅ Cleanup concluído');
  }

  /**
   * Cleanup de serviços específicos
   */
  cleanupServices(types: ServiceType[]): void {
    console.log(`🧹 Limpando serviços específicos: ${types.join(', ')}`);

    for (const type of types) {
      const service = this.services.get(type);
      if (service) {
        service.cleanup();
        console.log(`🧹 ${service.serviceName} limpo`);
      }
    }
  }

  /**
   * Verifica se todos os serviços estão inicializados
   */
  get allInitialized(): boolean {
    return this.isInitialized && Array.from(this.services.values()).every(s => s.isInitialized);
  }

  /**
   * Registra um novo serviço (para extensibilidade)
   */
  registerService<T extends ServiceType>(type: T, service: ServiceRegistry[T]): void {
    if (this.services.has(type)) {
      console.warn(`⚠️ Substituindo serviço existente: ${type}`);
    }
    
    this.services.set(type, service as BasePlatformService);
    console.log(`📋 Serviço ${type} registrado`);
  }

  /**
   * Remove um serviço do registro
   */
  unregisterService(type: ServiceType): void {
    const service = this.services.get(type);
    if (service) {
      service.cleanup();
      this.services.delete(type);
      console.log(`🗑️ Serviço ${type} removido`);
    }
  }
}

// Singleton instance para uso global
export const platformServiceFactory = PlatformServiceFactory.getInstance();
